import { Card, CardContent, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { ResourceUnavailable } from '@/components/molecules/resource-unavailable';
import { useAttachHostToDestinationGroup } from '@/features/destination-groups/hooks/mutation/use-attach-host-to-destination-group';
import { useRemoveHostFromDestinationGroup } from '@/features/destination-groups/hooks/mutation/use-remove-host-from-destination-group';
import { useDestinationGroups } from '@/features/destination-groups/hooks/query/use-destination-groups';

interface Props extends DialogActions {
  hostId: number;
  destinationGroupId: number;
}

export const UpdateHostDestinationGroupDialog = ({
  onCancel,
  open,
  destinationGroupId,
  hostId,
}: Props) => {
  const { t } = useTranslation(['hosts', 'common']);

  const [selectedDestinationGroupId, setSelectedDestinationGroupId] = useState(
    destinationGroupId || null,
  );

  // WE ARE FETCHING ONLY ONE DESTINATION GROUP HERE TO GET TOTAL NUMBER OF DESTINATION GROUPS
  const { data: destinationData } = useDestinationGroups({ page: 1, perPage: 1 });

  const { data } = useDestinationGroups({
    page: 1,
    perPage: destinationData?.total || 10,
    enabled: destinationData && destinationData?.total > 0,
  });

  const handlePickDestinationGroup = (id: number) => {
    if (id === selectedDestinationGroupId) {
      setSelectedDestinationGroupId(null);
    } else setSelectedDestinationGroupId(id);
  };

  const { mutateAsync: removeHostFromDestinationGroup, isPending: isRemoving } =
    useRemoveHostFromDestinationGroup();

  const { mutateAsync: attachHostToDestinationGroup, isPending: isAttaching } =
    useAttachHostToDestinationGroup();

  const onConfirm = async () => {
    try {
      if (destinationGroupId) {
        await removeHostFromDestinationGroup({ destinationGroupId, hostId });
      }

      if (selectedDestinationGroupId !== null) {
        await attachHostToDestinationGroup({
          destinationGroupId: selectedDestinationGroupId,
          hostId,
        });
      }

      onCancel?.();
    } catch {
      // Mutation hooks display errors; keep the dialog open.
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedDestinationGroupId(destinationGroupId || null);
    }
  }, [open, destinationGroupId]);

  return (
    <Dialog
      onCancel={onCancel}
      open={open}
      title={t('hosts:modal.title3')}
      variant="info"
      confirmButtonText={t('common:button.confirm')}
      onConfirm={onConfirm}
      isConfirmButtonDisabled={
        !hostId || selectedDestinationGroupId === (destinationGroupId || null)
      }
      isConfirmButtonSubmitting={isRemoving || isAttaching}>
      <Box sx={{ my: 1 }}>
        {data?.resources && data.resources?.length > 0 ? (
          data?.resources?.map(({ name, id }) => {
            return (
              <Card
                onClick={() => handlePickDestinationGroup(id)}
                key={id}
                sx={{
                  boxShadow: theme =>
                    selectedDestinationGroupId === id
                      ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                      : `0px 0px 0px 1px ${theme.palette.divider}`,
                  cursor: 'pointer',
                  mb: 1,
                }}>
                <CardContent
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  <Box>
                    <Typography color="textPrimary" variant="h6">
                      {name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <ResourceUnavailable />
        )}
      </Box>
    </Dialog>
  );
};
